import { Download, Link, Mail, Share, WhatsApp as WhatsAppIcon } from '@mui/icons-material';
import { Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import { createContext, type ReactNode, useContext, useState } from 'react';

import { Button } from '../Button/Button';

type ShareModalContextType = {
  url: string;
  subject: string;
  content: string;
  onShare?: (method: string) => void;
};

const ShareModalContext = createContext<ShareModalContextType | null>(null);

const useShareModalContext = () => {
  const context = useContext(ShareModalContext);
  if (!context) {
    throw new Error('Share modal components must be used within ShareModal');
  }
  return context;
};

export const ShareModal = ({
  children,
  title = 'Share report',
  url,
  subject,
  content,
  onShare,
  onClick,
}: {
  children: ReactNode;
  title?: string;
  url: string;
  subject: string;
  content: string;
  onShare?: (method: string) => void;
  onClick?: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <>
      <Button
        variant="primary"
        fullWidth
        onClick={() => {
          handleOpen();
          onClick?.();
        }}
        startIcon={<Share />}
      >
        Share
      </Button>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="share-modal-title"
        aria-describedby="share-modal-description"
        maxWidth="md"
      >
        <DialogTitle id="share-modal-title">{title}</DialogTitle>
        <DialogContent>
          <ShareModalContext.Provider value={{ content, onShare, subject, url }}>
            <Stack spacing={2.5} minWidth="200px">
              {children}
            </Stack>
          </ShareModalContext.Provider>
        </DialogContent>

        <DialogActions>
          <Button fullWidth variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const CopyLink = ({ onClick }: { onClick?: () => void }) => {
  const { url, onShare } = useShareModalContext();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    onShare?.('link');
    onClick?.();
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Button
      startIcon={<Link color="action" />}
      onClick={handleCopy}
      variant="text"
      sx={{ justifyContent: 'flex-start' }}
    >
      {copied ? 'Copied!' : 'Copy link'}
    </Button>
  );
};

ShareModal.CopyLink = CopyLink;

const Email = ({ onClick }: { onClick?: () => void }) => {
  const { subject, content, onShare } = useShareModalContext();

  const handleClick = () => {
    onShare?.('email');
    onClick?.();
  };

  return (
    <Button
      startIcon={<Mail color="action" />}
      variant="text"
      component="a"
      href={`mailto:?subject=${subject}&body=${encodeURIComponent(content)}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      sx={{ justifyContent: 'flex-start' }}
    >
      Email
    </Button>
  );
};

ShareModal.Email = Email;

const WhatsApp = ({ onClick }: { onClick?: () => void }) => {
  const { content, onShare } = useShareModalContext();

  const handleClick = () => {
    onShare?.('whatsapp');
    onClick?.();
  };

  return (
    <Button
      startIcon={<WhatsAppIcon color="action" />}
      variant="text"
      component="a"
      href={`https://wa.me/?text=${encodeURIComponent(content)}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      sx={{ justifyContent: 'flex-start' }}
    >
      WhatsApp
    </Button>
  );
};

ShareModal.WhatsApp = WhatsApp;

type Props = {
  onClick?: () => void;
  isGenerating: boolean;
};
const DownloadPDF = ({ onClick, isGenerating }: Props) => {
  const { onShare } = useShareModalContext();

  const handleClick = () => {
    onShare?.('download');
    onClick?.();
  };

  return (
    <Button
      startIcon={<Download color="action" />}
      variant="text"
      onClick={handleClick}
      sx={{ justifyContent: 'flex-start' }}
      disabled={isGenerating}
    >
      {isGenerating ? 'Generating PDF...' : 'Download PDF'}
    </Button>
  );
};

ShareModal.DownloadPDF = DownloadPDF;
